import { useState, useEffect, useRef } from "react";
import supabase from "../../lib/supabase";

function WorkspaceDeleteModal({ workspace, t, onCancel, onDeleted }: any) {
  // Impact counts are fetched on mount so the numbers shown are current at
  // the moment of confirmation, not at the moment the modal was opened.
  const [impact, setImpact] = useState<any>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const overlayRef = useRef(null);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.rpc(
        "count_workspace_impact", { p_workspace_id: workspace.id });
      if (error) { setError(error.message); return; }
      setImpact(Array.isArray(data) ? data[0] : data);
    })();
  }, [workspace.id]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape" && !deleting) onCancel(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onCancel, deleting]);

  const handleDelete = async () => {
    if (!acknowledged) return;
    setDeleting(true); setError("");
    const { error } = await supabase.rpc(
      "delete_workspace", { p_workspace_id: workspace.id });
    if (error) { setError(error.message); setDeleting(false); return; }
    onDeleted();   // parent: switch to Personal, refresh list, close modals
  };

  const mono = "'Courier New', monospace";
  const chassisCount = Number(impact?.chassis_count ?? 0);
  const memberCount = Number(impact?.member_count ?? 0);

  return (
    <div ref={overlayRef} onClick={(e) => { if (e.target === overlayRef.current && !deleting) onCancel(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1400,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", width: "100%", maxWidth: 440,
        boxShadow: "0 20px 60px rgba(0,0,0,0.35)" }}>

        {/* Header */}
        <div style={{ background: "#000", padding: "20px 24px" }}>
          <div style={{ fontFamily: mono, fontSize: 12, fontWeight: 900, color: "#cc0000",
            letterSpacing: "0.12em" }}>
            {t?.workspaceDeleteTitle || '⚠ DELETE WORKSPACE'}
          </div>
          <div style={{ fontSize: 18, fontWeight: 900, color: "#fff",
            fontFamily: "'Georgia', serif", marginTop: 6 }}>{workspace.name}</div>
        </div>

        <div style={{ padding: "20px 24px" }}>
          <p style={{ fontSize: 13, lineHeight: 1.6, color: "#333", margin: "0 0 18px 0" }}>
            {(t?.workspaceDeleteBody || 'You are about to permanently delete {name}. This action cannot be undone.')
              .replace('{name}', workspace.name)}
          </p>

          {/* Impact counts */}
          <div style={{ fontFamily: mono, fontSize: 9, color: "#aaa",
            letterSpacing: "0.12em", marginBottom: 8 }}>
            {t?.workspaceDeleteImpact || 'THIS WILL AFFECT:'}
          </div>
          <div style={{ border: "1px solid #e0e0e0", padding: "12px 14px", marginBottom: 18 }}>
            {impact === null && !error ? (
              <div style={{ fontFamily: mono, fontSize: 11, color: "#aaa" }}>Loading...</div>
            ) : (
              <>
                <div style={{ fontFamily: mono, fontSize: 12, color: "#000", fontWeight: 700 }}>
                  {(t?.workspaceDeleteChassis || '{n} chassis will be permanently deleted')
                    .replace('{n}', String(chassisCount))}
                </div>
                <div style={{ fontFamily: mono, fontSize: 12, color: "#000",
                  fontWeight: 700, marginTop: 6 }}>
                  {(t?.workspaceDeleteMembers || '{n} members will lose access')
                    .replace('{n}', String(memberCount))}
                </div>
              </>
            )}
          </div>

          {/* Acknowledgement */}
          <label style={{ display: "flex", alignItems: "flex-start", gap: 10,
            cursor: deleting ? "default" : "pointer", marginBottom: 18 }}>
            <input type="checkbox" checked={acknowledged} disabled={deleting}
              onChange={e => setAcknowledged(e.target.checked)}
              style={{ width: 16, height: 16, marginTop: 1, flexShrink: 0,
                cursor: deleting ? "default" : "pointer", accentColor: "#cc0000" }} />
            <span style={{ fontSize: 12, lineHeight: 1.5, color: "#333" }}>
              {t?.workspaceDeleteAck || 'I understand this workspace and all its chassis will be permanently deleted.'}
            </span>
          </label>

          {error && <div style={{ fontFamily: mono, fontSize: 10, color: "#cc0000",
            marginBottom: 12, lineHeight: 1.5 }}>{error}</div>}

          {/* Actions */}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onCancel} disabled={deleting}
              style={{ flex: 1, padding: "10px", background: "none", border: "1px solid #d0d0d0",
                cursor: deleting ? "not-allowed" : "pointer", fontFamily: mono, fontSize: 11,
                fontWeight: 900, letterSpacing: "0.12em", color: "#000" }}>
              {t?.workspaceDeleteCancel || 'CANCEL'}
            </button>
            <button onClick={handleDelete} disabled={!acknowledged || deleting}
              style={{ flex: 1, padding: "10px", border: "none",
                background: acknowledged && !deleting ? "#cc0000" : "#e8e8e8",
                color: acknowledged && !deleting ? "#fff" : "#aaa",
                cursor: acknowledged && !deleting ? "pointer" : "not-allowed",
                fontFamily: mono, fontSize: 11, fontWeight: 900, letterSpacing: "0.12em" }}>
              {deleting ? "DELETING..." : (t?.workspaceDeleteConfirm || 'DELETE PERMANENTLY')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { WorkspaceDeleteModal };
